import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft, Save, Sparkles, Loader2, Plus, X, EyeOff, ImagePlus, Eye,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const emptyPost = {
  title: "", subtitle: "", body_markdown: "", meta_description: "",
  hero_image_url: "", hero_image_alt: "", hero_image_attribution: "",
  tags: [], city: "", status: "draft",
  ai_generated: false, ai_prompt: "", source_urls: [],
};

const AdminBlogEdit = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  useSeo({ title: isNew ? "Nieuwe post — Admin" : "Post bewerken — Admin", robots: "noindex,nofollow" });

  // Read a curated idea (set by AdminBlog) so the AI dialog opens prefilled.
  const idea = useMemo(() => {
    if (!isNew) return null;
    try {
      const raw = sessionStorage.getItem("wa_blog_idea");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      sessionStorage.removeItem("wa_blog_idea");
      return parsed;
    } catch {
      return null;
    }
  }, [isNew]);

  const [post, setPost] = useState(emptyPost);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(isNew);
  const [preview, setPreview] = useState(false);
  const [imageSuggestions, setImageSuggestions] = useState([]);
  const [imgSearching, setImgSearching] = useState(false);
  const [imgQuery, setImgQuery] = useState("");

  const set = (k, v) => setPost((p) => ({ ...p, [k]: v }));
  const addTag = (t) => { const v = t.trim(); if (v && !post.tags.includes(v)) set("tags", [...post.tags, v]); };
  const removeTag = (t) => set("tags", post.tags.filter((x) => x !== t));

  useEffect(() => {
    if (isNew) return;
    api(`/admin/blog/${id}`).then((p) => { setPost({ ...emptyPost, ...p, tags: p.tags || [], source_urls: p.source_urls || [] }); setLoading(false); })
      .catch(() => { toast.error("Kon post niet laden"); navigate("/admin/blog"); });
  }, [id, isNew, navigate]);

  const save = async (publish = false) => {
    if (!post.title.trim()) return toast.error("Titel is verplicht");
    if (!post.body_markdown.trim()) return toast.error("Body mag niet leeg zijn");
    setSaving(true);
    try {
      const body = {
        ...post,
        status: publish ? "published" : (post.status || "draft"),
      };
      const url = isNew ? "/admin/blog" : `/admin/blog/${id}`;
      const method = isNew ? "POST" : "PUT";
      const res = await api(url, { method, body: JSON.stringify(body) });
      toast.success(publish ? "Post is live!" : "Opgeslagen");
      if (isNew) navigate(`/admin/blog/${res.id}`);
      else setPost({ ...emptyPost, ...res, tags: res.tags || [], source_urls: res.source_urls || [] });
    } catch (e) {
      toast.error(e.message || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6" data-testid="admin-blog-edit">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link to="/admin/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-navy">
            <ArrowLeft size={14} /> Terug naar overzicht
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setPreview((v) => !v)} className="rounded-full" data-testid="toggle-preview">
              {preview ? <><EyeOff size={14} className="mr-1" /> Bewerken</> : <><Eye size={14} className="mr-1" /> Voorbeeld</>}
            </Button>
            <Button variant="outline" onClick={() => setAiOpen(true)} className="rounded-full text-amber-600" data-testid="reopen-ai">
              <Sparkles size={14} className="mr-1" /> AI genereren
            </Button>
            <Button variant="outline" onClick={() => save(false)} disabled={saving} className="rounded-full" data-testid="save-draft">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1" /> Opslaan</>}
            </Button>
            <Button onClick={() => save(true)} disabled={saving} className="rounded-full bg-navy text-amber-300 hover:bg-navy/90" data-testid="publish-btn">
              {post.status === "published" ? "Update live" : "Publiceer"}
            </Button>
          </div>
        </div>

        <h1 className="mt-3 font-display text-2xl font-extrabold text-navy">
          {isNew ? "Nieuwe blogpost" : post.title || "Post bewerken"}
        </h1>

        {preview ? (
          <PreviewCard post={post} />
        ) : (
          <div className="mt-5 grid gap-5 lg:grid-cols-[1.6fr_1fr]">
            {/* Left: main content */}
            <div className="space-y-4">
              <Field label="Titel *" testid="title-input">
                <Input value={post.title} onChange={(e) => set("title", e.target.value)} placeholder="Waarom Amsterdam nog steeds..." className="rounded-xl" />
              </Field>
              <Field label="Subtitel" testid="subtitle-input">
                <Input value={post.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Een korte samenvatting…" className="rounded-xl" />
              </Field>
              <Field label="Meta description (SEO, max 155 tekens)" testid="meta-input">
                <Textarea rows={2} value={post.meta_description} onChange={(e) => set("meta_description", e.target.value.slice(0, 200))} className="rounded-xl" />
                <div className="mt-1 text-[11px] text-slate-400">{(post.meta_description || "").length}/200</div>
              </Field>
              <Field label="Body (Markdown ondersteund)" testid="body-input">
                <Textarea rows={22} value={post.body_markdown} onChange={(e) => set("body_markdown", e.target.value)}
                  className="rounded-xl font-mono text-sm leading-relaxed" placeholder="## Inleiding&#10;&#10;Schrijf hier je artikel…" />
              </Field>
            </div>

            {/* Right: meta */}
            <div className="space-y-4">
              <div className="rounded-2xl border border-border bg-white p-4">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Hero afbeelding</Label>
                {post.hero_image_url ? (
                  <img src={post.hero_image_url} alt={post.hero_image_alt} className="mt-2 aspect-[16/9] w-full rounded-xl object-cover" />
                ) : (
                  <div className="mt-2 flex aspect-[16/9] w-full items-center justify-center rounded-xl bg-slate-50 text-slate-300"><ImagePlus size={30} /></div>
                )}
                {post.hero_image_attribution && (
                  <p className="mt-1 text-[10px] italic text-slate-400" data-testid="hero-attribution">{post.hero_image_attribution}</p>
                )}
                <Input className="mt-2 rounded-xl text-xs" placeholder="Foto-URL (auto-gevuld door AI)" value={post.hero_image_url} onChange={(e) => set("hero_image_url", e.target.value)} data-testid="hero-url" />
                <Input className="mt-2 rounded-xl text-xs" placeholder="Alt-tekst" value={post.hero_image_alt} onChange={(e) => set("hero_image_alt", e.target.value)} data-testid="hero-alt" />

                {/* Auto-suggested images from AI generate */}
                {imageSuggestions.length > 0 && (
                  <div className="mt-3">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Kies een andere foto ({imageSuggestions.length} rechtenvrij)</Label>
                    <div className="mt-1 grid grid-cols-3 gap-1">
                      {imageSuggestions.map((img) => (
                        <button
                          key={img.url}
                          type="button"
                          data-testid={`img-suggest-${img.url.slice(-16)}`}
                          onClick={() => setPost((p) => ({
                            ...p,
                            hero_image_url: img.url,
                            hero_image_alt: img.title || p.hero_image_alt,
                            hero_image_attribution: img.attribution || "",
                          }))}
                          title={img.title}
                          className={`group relative overflow-hidden rounded-lg border-2 transition ${post.hero_image_url === img.url ? "border-brandblue" : "border-transparent hover:border-slate-300"}`}
                        >
                          <img src={img.thumbnail || img.url} alt="" className="aspect-square w-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual search fallback */}
                <div className="mt-3">
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!imgQuery.trim()) return;
                    setImgSearching(true);
                    try {
                      const res = await api("/admin/blog/images/search", {
                        method: "POST",
                        body: JSON.stringify({ query: imgQuery, limit: 6 }),
                      });
                      setImageSuggestions(res.results || []);
                      if (!res.results?.length) toast.info("Geen resultaten");
                    } catch (er) {
                      toast.error(er.message || "Zoeken mislukt");
                    } finally { setImgSearching(false); }
                  }} className="flex gap-1">
                    <Input value={imgQuery} onChange={(e) => setImgQuery(e.target.value)}
                      placeholder="Zoek eigen foto (bv. 'gracht')" className="rounded-full text-xs h-8" data-testid="img-search-q" />
                    <Button type="submit" size="sm" variant="outline" className="rounded-full" disabled={imgSearching} data-testid="img-search-btn">
                      {imgSearching ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                    </Button>
                  </form>
                  <p className="mt-2 text-[10px] leading-tight text-slate-400">Foto&apos;s komen van Openverse (CC0/BY/PDM). Rechtenvrij voor commercieel gebruik. Attributie wordt automatisch getoond.</p>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-white p-4">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Metadata</Label>
                <div className="mt-2">
                  <Label className="text-xs">Stad (optioneel)</Label>
                  <Input className="mt-1 rounded-xl" placeholder="Amsterdam" value={post.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="city-input" />
                </div>
                <div className="mt-3">
                  <Label className="text-xs">Tags</Label>
                  <TagInput tags={post.tags} onAdd={addTag} onRemove={removeTag} />
                </div>
                <div className="mt-3">
                  <Label className="text-xs">Status</Label>
                  <div className="mt-1 inline-flex rounded-full bg-slate-100 p-1">
                    <button type="button" onClick={() => set("status", "draft")} data-testid="status-draft"
                      className={`rounded-full px-3 py-1 text-xs font-bold ${post.status === "draft" ? "bg-navy text-white" : "text-slate-500"}`}>Concept</button>
                    <button type="button" onClick={() => set("status", "published")} data-testid="status-pub"
                      className={`rounded-full px-3 py-1 text-xs font-bold ${post.status === "published" ? "bg-emerald-600 text-white" : "text-slate-500"}`}>Live</button>
                  </div>
                </div>
              </div>

              {post.source_urls?.length > 0 && (
                <div className="rounded-2xl border border-border bg-white p-4">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Bronnen (voor jouw referentie)</Label>
                  <ul className="mt-2 space-y-1">
                    {post.source_urls.map((u) => (
                      <li key={u} className="truncate text-xs">
                        <a href={u} target="_blank" rel="noreferrer" className="text-brandblue hover:underline">{u}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {aiOpen && (
        <AIGenerateDialog
          initialIdea={idea}
          onClose={() => setAiOpen(false)}
          onApply={(draft, sourceUrls, prompt, imageSuggestions) => {
            setPost((p) => ({
              ...p,
              title: draft.title || p.title,
              subtitle: draft.subtitle || p.subtitle,
              meta_description: (draft.meta_description || p.meta_description).slice(0, 200),
              body_markdown: draft.body_markdown || p.body_markdown,
              tags: draft.tags?.length ? draft.tags : p.tags,
              hero_image_url: draft.hero_image_url || p.hero_image_url,
              hero_image_alt: draft.hero_image_alt || p.hero_image_alt,
              hero_image_attribution: draft.hero_image_attribution || "",
              source_urls: sourceUrls,
              ai_generated: true,
              ai_prompt: prompt,
            }));
            setImageSuggestions(imageSuggestions || []);
            setAiOpen(false);
            toast.success(imageSuggestions?.length
              ? `Draft klaar met ${imageSuggestions.length} passende foto's`
              : "Draft geladen — pas hem aan en publiceer");
          }}
        />
      )}
    </div>
  );
};

const Field = ({ label, testid, children }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</Label>
    <div className="mt-1" data-testid={testid}>{children}</div>
  </div>
);

const TagInput = ({ tags, onAdd, onRemove }) => {
  const [val, setVal] = useState("");
  const submit = (e) => { e.preventDefault(); if (val.trim()) { onAdd(val); setVal(""); } };
  return (
    <div>
      <div className="mt-1 flex flex-wrap gap-1">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs">
            {t}
            <button type="button" onClick={() => onRemove(t)} className="text-slate-400 hover:text-danger"><X size={10} /></button>
          </span>
        ))}
      </div>
      <form onSubmit={submit} className="mt-1 flex gap-1">
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Voeg tag toe…" className="rounded-full text-xs h-8" />
        <Button type="submit" variant="outline" className="rounded-full" size="sm"><Plus size={13} /></Button>
      </form>
    </div>
  );
};

const PreviewCard = ({ post }) => (
  <article className="mt-6 rounded-3xl bg-white p-6 shadow-sm sm:p-10">
    {post.hero_image_url && (
      <img src={post.hero_image_url} alt={post.hero_image_alt} className="aspect-[16/9] w-full rounded-2xl object-cover" />
    )}
    <div className="mt-4 flex flex-wrap gap-1 text-[11px]">
      {(post.tags || []).map((t) => <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5">{t}</span>)}
    </div>
    <h1 className="mt-3 font-display text-3xl font-extrabold text-navy sm:text-4xl">{post.title || "Titel voorbeeld"}</h1>
    {post.subtitle && <p className="mt-2 text-lg text-slate-500">{post.subtitle}</p>}
    <div className="prose prose-slate mt-6 max-w-none prose-lg prose-headings:font-display prose-headings:text-navy prose-h2:mt-10 prose-h2:mb-4 prose-h2:text-2xl prose-h3:mt-8 prose-h3:mb-3 prose-p:my-5 prose-p:leading-[1.75] prose-p:text-slate-700 prose-li:my-1.5 first:prose-p:text-lg first:prose-p:text-slate-600">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_markdown || "*Body verschijnt hier…*"}</ReactMarkdown>
    </div>
  </article>
);

const AIGenerateDialog = ({ onClose, onApply, initialIdea = null }) => {
  const [topic, setTopic] = useState(initialIdea?.topic || "");
  const [city, setCity] = useState(initialIdea?.city || "");
  const [tags, setTags] = useState((initialIdea?.tags || []).join(", "));
  const [targetLen, setTargetLen] = useState(initialIdea?.target_length_words || 900);
  const [urls, setUrls] = useState(initialIdea?.source_urls?.length ? [...initialIdea.source_urls, ""] : [""]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");

  const validUrls = urls.map((u) => u.trim()).filter((u) => u.startsWith("http"));

  const generate = async () => {
    if (!topic.trim()) return toast.error("Onderwerp is verplicht");
    setBusy(true);
    setProgress("Starten…");
    try {
      const started = await api("/admin/blog/generate", {
        method: "POST",
        body: JSON.stringify({
          topic: topic.trim(),
          city: city.trim() || null,
          tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
          source_urls: validUrls,
          target_length_words: Number(targetLen) || 900,
        }),
      });
      const jobId = started.job_id;
      // Poll for up to 3 minutes, every 2s
      const PROGRESS_LABELS = {
        queued: "In wachtrij…",
        scraping: "Bronnen lezen…",
        writing: "AI schrijft (~20s)…",
        finding_images: "Foto's zoeken…",
        done: "Klaar!",
      };
      const t0 = Date.now();
      while (Date.now() - t0 < 180_000) {
        await new Promise((r) => setTimeout(r, 2000));
        const s = await api(`/admin/blog/generate/status/${jobId}`);
        setProgress(PROGRESS_LABELS[s.progress] || s.progress || s.status);
        if (s.status === "done") {
          onApply(s.draft, validUrls, topic.trim(), s.image_suggestions || []);
          return;
        }
        if (s.status === "failed") {
          throw new Error(s.error || "AI generatie mislukt");
        }
      }
      throw new Error("Time-out (langer dan 3 minuten)");
    } catch (e) {
      toast.error(e.message || "AI generatie mislukt");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-4 top-4 rounded-full p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">
          <Sparkles size={12} /> AI Artikel Generator
        </div>
        <h2 className="font-display text-2xl font-extrabold text-navy">Genereer een blogpost</h2>
        <p className="mt-1 text-sm text-slate-500">
          Geef een onderwerp op en (optioneel) bron-URL&apos;s van bestaande artikelen. De AI leest die door
          en schrijft een menselijke tekst in de stijl van WoningAlert.
        </p>

        <div className="mt-5 space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Onderwerp *</Label>
            <Input className="mt-1 rounded-xl" value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="Bijv: Hoe herken je een huurscam in Amsterdam?" data-testid="ai-topic" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Stad (optioneel)</Label>
              <Input className="mt-1 rounded-xl" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Amsterdam" data-testid="ai-city" />
            </div>
            <div>
              <Label className="text-xs">Doellengte (woorden)</Label>
              <Input type="number" min={400} max={1800} className="mt-1 rounded-xl" value={targetLen} onChange={(e) => setTargetLen(e.target.value)} data-testid="ai-length" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Tags (komma-gescheiden)</Label>
            <Input className="mt-1 rounded-xl" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="scam, veiligheid, tips" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Bron-URL&apos;s (voor feiten &amp; actualiteit)</Label>
            <p className="mt-0.5 text-[11px] text-slate-400">Plak links van recente artikelen (NOS, NRC, Funda-blog, etc.). Max 5.</p>
            {urls.map((u, i) => (
              <div key={i} className="mt-1 flex gap-1">
                <Input className="rounded-xl" value={u} onChange={(e) => { const n = [...urls]; n[i] = e.target.value; setUrls(n); }}
                  placeholder="https://…" data-testid={`ai-url-${i}`} />
                {urls.length > 1 && (
                  <Button type="button" variant="outline" onClick={() => setUrls(urls.filter((_, j) => j !== i))} className="rounded-full"><X size={13} /></Button>
                )}
              </div>
            ))}
            {urls.length < 5 && (
              <Button type="button" variant="outline" onClick={() => setUrls([...urls, ""])} className="mt-1 rounded-full text-xs" size="sm">
                <Plus size={12} className="mr-1" /> URL toevoegen
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Annuleren</Button>
          <Button onClick={generate} disabled={busy} className="rounded-full bg-navy text-amber-300 hover:bg-navy/90" data-testid="ai-generate-btn">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> {progress || "AI schrijft…"}</> : <><Sparkles size={14} className="mr-1" /> Genereer artikel</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogEdit;
